'use client';

import { type DialogProps, Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { useRouter } from 'next/navigation';
import { useIntl } from 'react-intl';
import type { SelectValuesFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { DialogHeader } from '../../Dialog/DialogHeader/DialogHeader.client';
import { VariationRow } from './VariationRow/VariationRow.client';
import type { VariationSelectionOnSelectHandler } from '../types';

export type VariationSelectionDialogProps = VariationSelectionOnSelectHandler & {
  data: SelectValuesFragmentFragment;
  open: boolean;
  onClose: NonNullable<DialogProps['onClose']>;
};

/**
 * VariationSelectionDialog component
 *
 * dialog to select a different variation
 * */
export const VariationSelectionDialog = ({
  data,
  open,
  onClose,
  onSelect,
}: VariationSelectionDialogProps) => {
  const { values, displayName } = data;
  const { formatMessage } = useIntl();
  /* this is needed to tell the VariationRow if it should reserve the space for the image or not */
  const atLeastOneDimensionValueHasAnImage = values.some(
    (value) => value.image !== null || value.rgb !== null || value.variationMainImage !== null,
  );

  return (
    <Dialog isOpen={open} onClose={onClose} className="w-full max-w-[600px]">
      <DialogHeader onClose={onClose}>
        {formatMessage(
          {
            id: 'product.variationSelect.select.dialog.headline',
            defaultMessage: 'Bitte {dimension} wählen',
          },
          {
            dimension: displayName,
          },
        )}
      </DialogHeader>
      <DialogContent className="p-0">
        <ul className="list-none">
          {values.map((value) => (
            <li key={value.sku}>
              <VariationRow
                data={value}
                renderImage={atLeastOneDimensionValueHasAnImage}
                onClose={onClose}
                onSelect={onSelect}
                key={value.sku}
              />
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
};
